import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { authAPI } from '../services/api';
import { toast } from '../hooks/use-toast';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      let response;
      if (isLogin) {
        // Login
        response = await authAPI.login({
          email: formData.email,
          password: formData.password
        });
      } else {
        // Register
        if (!formData.name) {
          toast({
            title: 'Error',
            description: 'Por favor completa todos los campos',
            variant: 'destructive'
          });
          setLoading(false);
          return;
        }
        
        response = await authAPI.register({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: 'buyer'
        });
      }

      // Save user data
      login(response.user);
      
      // Check if user is a delivery driver - redirect to delivery portal
      if (response.user?.role === 'delivery_driver') {
        localStorage.setItem('delivery_token', localStorage.getItem('token'));
        localStorage.setItem('delivery_user', JSON.stringify(response.user));
        toast({
          title: 'Portal de Repartidores',
          description: 'Redirigiendo al portal de repartidores...',
        });
        navigate('/delivery');
        return;
      }
      
      toast({
        title: isLogin ? '¡Bienvenido!' : '¡Cuenta creada!',
        description: isLogin ? 'Sesión iniciada correctamente' : 'Tu cuenta ha sido creada exitosamente',
      });
      
      navigate(redirectTo);
    } catch (error) {
      console.error('Auth error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Ocurrió un error al procesar tu solicitud',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-zl-orange-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">
            <img src="/logo-zonalibre.png" alt="Zona Libre" className="h-12 mx-auto" />
          </h1>
          <p className="text-gray-600">
            {isLogin ? 'Ingresa a tu cuenta' : 'Crea tu cuenta'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div>
              <Label htmlFor="name">Nombre completo</Label>
              <Input
                id="name"
                type="text"
                placeholder="Juan Pérez"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required={!isLogin}
              />
            </div>
          )}

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="tu@email.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="password">Contraseña</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-zl-orange hover:bg-zl-orange-600 text-white h-11"
            disabled={loading}
          >
            {loading ? 'Procesando...' : (isLogin ? 'Ingresar' : 'Crear cuenta')}
          </Button>
        </form>

        <div className="mt-6 text-center space-y-2">
          <button
            onClick={() => setIsLogin(!isLogin)}
            className="text-zl-orange hover:underline"
          >
            {isLogin ? '¿No tienes cuenta? Regístrate' : '¿Ya tienes cuenta? Ingresa'}
          </button>
          {isLogin && (
            <p className="text-sm text-gray-500">
              O <Link to="/register" className="text-zl-orange hover:underline font-medium">crea tu cuenta completa</Link>
            </p>
          )}
        </div>

        <div className="mt-8 pt-6 border-t text-center text-sm text-gray-600">
          <p>Al continuar, aceptas nuestros Términos y Condiciones</p>
        </div>
      </Card>
    </div>
  );
};

export default Login;
